import apiClient from '@/scripts/api/apiClient';

/**
 * Role API endpoints
 */

// Query key factories
export const roleKeys = {
  getRoles: () => ['get', 'roles'],
  getRole: (id) => ['get', 'roles', id],
  getRolePermissions: (id) => ['get', 'roles', id, 'permissions'],
  createRole: () => ['create', 'roles'],
  updateRole: (id) => ['update', 'roles', id],
  deleteRole: (id) => ['delete', 'roles', id],
  updateRolePermissions: (id) => ['update', 'roles', id, 'permissions'],
};

/**
 * Get all roles
 * @returns {Promise<Array>} List of roles
 */
export const getRoles = async () => {
  const response = await apiClient.get('/api/roles');
  return response.data;
};

/**
 * Get a specific role by ID
 * @param {string} id - Role ID
 * @returns {Promise<Object>} Role details
 */
export const getRole = async (id) => {
  const response = await apiClient.get(`/api/roles/${id}`);
  return response.data;
};

/**
 * Get permissions for a specific role
 * @param {string} id - Role ID
 * @returns {Promise<Array>} List of permissions assigned to the role
 */
export const getRolePermissions = async (id) => {
  const response = await apiClient.get(`/api/roles/${id}/permissions`);
  return response.data;
};

/**
 * Create a new role
 * @param {Object} roleData - Role data including name and description
 * @returns {Promise<Object>} Created role
 */
export const createRole = async (roleData) => {
  const response = await apiClient.post('/api/roles', roleData);
  return response.data;
};

/**
 * Update an existing role
 * @param {string} id - Role ID
 * @param {Object} roleData - Role data to update
 * @returns {Promise<Object>} Updated role
 */
export const updateRole = async (id, roleData) => {
  const response = await apiClient.put(`/api/roles/${id}`, roleData);
  return response.data;
};

/**
 * Delete a role
 * @param {string} id - Role ID
 * @returns {Promise<Object>} Response data
 */
export const deleteRole = async (id) => {
  const response = await apiClient.delete(`/api/roles/${id}`);
  return response.data;
};

/**
 * Update permissions for a role
 * @param {string} roleId - Role ID
 * @param {Array<string>} permissionIds - List of permission IDs to assign to the role
 * @returns {Promise<Array>} Updated list of permissions for the role
 */
export const updateRolePermissions = async (roleId, permissionIds) => {
  const response = await apiClient.put(`/api/roles/${roleId}/permissions`, { permission_ids: permissionIds });
  return response.data;
};
